import { useState, useEffect, useCallback } from "react";
import {
  View, Text, ScrollView, Pressable,
  ActivityIndicator,
} from "react-native";
import styled from "styled-components/native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";
import {
  doc, onSnapshot, collection, query, where, getDocs, getDoc,
  setDoc, updateDoc, deleteDoc, Timestamp, writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useAvailability } from "@/hooks/useAvailability";
import { createPost } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import type { Match, Selection } from "@/lib/types";
import { LockIcon, CloseCircleFilledIcon, CheckmarkIcon } from "@/components/Icons";

/* ── Helpers ── */

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return dateStr; }
}

function formatTime(timeStr: string): string {
  try {
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    if (hour >= 12) return `${hour === 12 ? 12 : hour - 12}:${m}pm`;
    return `${hour === 0 ? 12 : hour}:${m}am`;
  } catch { return timeStr; }
}

function teamPriority(team: string): number {
  const match = team.match(/(\d+)/);
  return match ? parseInt(match[1]) : 99;
}

/* ── Styled Components ── */

const Screen = styled.ScrollView`
  flex: 1;
  background-color: ${colors.bg};
`;

const Center = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const MatchHeader = styled.View`
  padding-horizontal: 20px;
  padding-top: 16px;
  padding-bottom: 20px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

const TeamBadge = styled.View`
  background-color: ${colors.bg};
  border-radius: ${radius.sm}px;
  padding-horizontal: 10px;
  padding-vertical: 4px;
  border-width: 1px;
  border-color: ${colors.line};
  align-self: flex-start;
  margin-bottom: 8px;
`;

const TeamBadgeText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.inkSecondary};
`;

const Opponent = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
`;

const MatchMeta = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  font-weight: ${font.medium};
  margin-top: 4px;
`;

const Section = styled.View`
  padding-horizontal: 20px;
  margin-top: 20px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const CounterRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 20px;
  padding-vertical: 14px;
  margin-top: 16px;
  background-color: ${colors.white};
  border-bottom-width: 1px;
  border-top-width: 1px;
  border-color: ${colors.line};
`;

const CounterText = styled.Text`
  font-size: 16px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const CounterNumber = styled.Text<{ full: boolean }>`
  font-size: 16px;
  font-weight: ${font.extrabold};
  color: ${(p) => (p.full ? colors.brand : colors.inkSecondary)};
`;

const CounterSub = styled.Text`
  font-size: 13px;
  font-weight: ${font.medium};
  color: ${colors.amber};
  margin-left: 6px;
`;

const PlayerCard = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const PlayerRow = styled.Pressable<{ hasBorder: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  border-bottom-width: ${(p) => (p.hasBorder ? 1 : 0)}px;
  border-bottom-color: ${colors.line};
`;

const PlayerAvatar = styled.View<{ bg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  background-color: ${(p) => p.bg};
`;

const PlayerAvatarText = styled.Text<{ color: string }>`
  font-size: 13px;
  font-weight: ${font.extrabold};
  color: ${(p) => p.color};
`;

const PlayerName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const PlayerNote = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const HigherTeamPill = styled.View`
  background-color: ${colors.amber50};
  border-radius: ${radius.full}px;
  padding-horizontal: 8px;
  padding-vertical: 3px;
  border-width: 1px;
  border-color: ${colors.amber200};
  margin-right: 8px;
`;

const HigherTeamPillText = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const Flex1 = styled.View`
  flex: 1;
`;

const CheckBox = styled.View<{ checked: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border-width: 2px;
  border-color: ${(p) => (p.checked ? colors.brand : colors.lineHover)};
  background-color: ${(p) => (p.checked ? colors.brand : colors.white)};
  align-items: center;
  justify-content: center;
`;

const ConfirmButton = styled.Pressable<{ disabled: boolean }>`
  background-color: ${(p) => (p.disabled ? colors.lineHover : colors.brand)};
  border-radius: ${radius.md}px;
  padding-vertical: 16px;
  align-items: center;
  justify-content: center;
  margin-horizontal: 20px;
  margin-top: 24px;
`;

const ConfirmButtonText = styled.Text<{ disabled: boolean }>`
  font-size: 16px;
  font-weight: ${font.bold};
  color: ${(p) => (p.disabled ? colors.inkMuted : colors.white)};
`;

const BottomSpacer = styled.View`
  height: 40px;
`;

const EmptySection = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  padding: 12px 0;
`;

const AccessDenied = styled.Text`
  font-size: 15px;
  color: ${colors.danger};
  font-weight: ${font.semibold};
  text-align: center;
  margin-top: 8px;
`;

/* ── Withdrawal-specific styled components ── */

const WithdrawnPlayerRow = styled.View<{ hasBorder: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  border-bottom-width: ${(p) => (p.hasBorder ? 1 : 0)}px;
  border-bottom-color: ${colors.line};
  opacity: 0.5;
`;

const WithdrawnName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-decoration-line: line-through;
`;

const WithdrawnReasonText = styled.Text`
  font-size: 12px;
  color: ${colors.danger};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const EmptySlotRow = styled.View<{ hasBorder: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  border-bottom-width: ${(p) => (p.hasBorder ? 1 : 0)}px;
  border-bottom-color: ${colors.line};
`;

const EmptySlotDot = styled.View`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  background-color: ${colors.dangerLight};
  border-width: 2px;
  border-style: dashed;
  border-color: #fecaca;
`;

const EmptySlotText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.danger};
`;

/* ── Component ── */

export default function TeamSelectionScreen() {
  const { matchId } = useLocalSearchParams<{ matchId: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();

  const [match, setMatch] = useState<(Match & { id: string }) | null>(null);
  const [matchLoading, setMatchLoading] = useState(true);
  const { available, maybe, loading: availLoading } = useAvailability(club?.id, matchId);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [higherTeamSelections, setHigherTeamSelections] = useState<Record<string, string>>({});
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const { alertProps, alert } = useAlert();

  // Withdrawal tracking
  const [withdrawnPlayers, setWithdrawnPlayers] = useState<Array<Selection & { id: string }>>([]);

  // Load match doc
  useEffect(() => {
    if (!club?.id || !matchId) return;
    const unsub = onSnapshot(doc(db, "clubs", club.id, "matches", matchId), (snap) => {
      if (snap.exists()) {
        setMatch({ id: snap.id, ...snap.data() } as Match & { id: string });
      }
      setMatchLoading(false);
    });
    return unsub;
  }, [club?.id, matchId]);

  // Check access: admin or captain/vice-captain for this team
  useEffect(() => {
    if (!club?.id || !match || !user || !membership) return;

    // Admins always have access
    if (membership.role === "admin") {
      setHasAccess(true);
      return;
    }

    // Check teamConfig for captain/vice-captain
    const teamSlug = match.team.toLowerCase().replace(/\s+/g, "-");
    const configRef = doc(db, "clubs", club.id, "teamConfig", teamSlug);
    const unsub = onSnapshot(configRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const captainId = data?.captainId;
        const viceCaptainId = data?.viceCaptainId;
        setHasAccess(user.uid === captainId || user.uid === viceCaptainId);
      } else {
        setHasAccess(false);
      }
    });
    return unsub;
  }, [club?.id, match?.team, user?.uid, membership?.role]);

  // Load existing selections (so reopening the screen shows previous picks)
  // Also track withdrawn players
  useEffect(() => {
    if (!club?.id || !matchId) return;
    const unsub = onSnapshot(
      collection(db, "clubs", club.id, "matches", matchId, "selections"),
      (snap) => {
        const sel = new Set<string>();
        const withdrawn: Array<Selection & { id: string }> = [];
        snap.docs.forEach((d) => {
          const data = d.data() as Selection;
          if (data.selected && !data.withdrawn) {
            sel.add(d.id);
          }
          if (data.selected && data.withdrawn) {
            withdrawn.push({ id: d.id, ...data });
          }
        });
        setSelected(sel);
        setWithdrawnPlayers(withdrawn);
      }
    );
    return unsub;
  }, [club?.id, matchId]);

  // Check higher priority team selections on the same date
  useEffect(() => {
    if (!club?.id || !match) return;

    const currentPriority = teamPriority(match.team);

    // Find all matches on the same date
    const matchesRef = collection(db, "clubs", club.id, "matches");
    const q = query(matchesRef, where("date", "==", match.date));

    const unsub = onSnapshot(q, async (snap) => {
      const higherMatches = snap.docs
        .filter((d) => {
          const data = d.data();
          return d.id !== matchId && teamPriority(data.team) < currentPriority;
        });

      const mapping: Record<string, string> = {};
      for (const m of higherMatches) {
        const selSnap = await getDocs(
          collection(db, "clubs", club.id, "matches", m.id, "selections")
        );
        selSnap.docs.forEach((s) => {
          if (s.data().selected) {
            mapping[s.id] = m.data().team;
          }
        });
      }
      setHigherTeamSelections(mapping);
    });

    return unsub;
  }, [club?.id, match?.date, match?.team, matchId]);

  // All selectable players = available + maybe, excluding withdrawn players who are already selected
  const withdrawnIds = new Set(withdrawnPlayers.map((w) => w.userId));
  const allPlayers = [...available, ...maybe];

  const togglePlayer = useCallback((userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const isReplacementMode = match?.status === "selected" && withdrawnPlayers.length > 0;

  const handleConfirm = async () => {
    if (!club || !user || !membership || !match || !matchId) return;
    if (selected.size === 0) return;

    setSaving(true);
    try {
      const batch = writeBatch(db);

      // Create/update selection docs for selected players
      for (const player of allPlayers) {
        const selRef = doc(db, "clubs", club.id, "matches", matchId, "selections", player.userId);
        if (selected.has(player.userId)) {
          batch.set(selRef, {
            userId: player.userId,
            displayName: player.displayName,
            selected: true,
            selectedBy: user.uid,
            selectedByName: membership.displayName,
            selectedAt: Timestamp.now(),
          });
        }
      }

      // Create the announcement feed post first so we can link the notification to it
      const selectedPlayers = allPlayers
        .filter((p) => selected.has(p.userId))
        .map((p) => p.displayName);
      const postText = `\ud83c\udfcf ${match.team} vs ${match.opponent} \u2014 Team confirmed\n\n${selectedPlayers.map((name, i) => `${i + 1}. ${name}`).join("\n")}`;
      const postRef = await createPost(club.id, {
        authorId: user.uid,
        authorName: membership.displayName,
        authorRole: membership.role,
        text: postText,
        visibility: "internal",
        clubName: club.name,
      });

      // Update match status (with link back to the post)
      const matchRef = doc(db, "clubs", club.id, "matches", matchId);
      batch.update(matchRef, {
        status: "selected",
        confirmedBy: user.uid,
        selectionPostId: postRef.id,
      });

      await batch.commit();

      // If replacement mode, handle withdrawn player updates and fee swaps
      if (isReplacementMode) {
        // Find new players who weren't in the original selection (replacements)
        const newlySelected = allPlayers.filter(
          (p) => selected.has(p.userId) && !withdrawnIds.has(p.userId)
        );

        // For each withdrawn player, find a replacement and update docs
        for (let i = 0; i < withdrawnPlayers.length && i < newlySelected.length; i++) {
          const withdrawn = withdrawnPlayers[i];
          const replacement = newlySelected[i];

          // Update withdrawn player's selection doc with replacedBy
          const withdrawnSelRef = doc(db, "clubs", club.id, "matches", matchId, "selections", withdrawn.userId);
          await updateDoc(withdrawnSelRef, {
            replacedBy: replacement.userId,
            replacedByName: replacement.displayName,
          });

          // Only handle fees if club has Stripe Connect set up
          const canCollectFees = club.collectMatchFees && club.stripeOnboardingComplete;

          // Delete withdrawn player's fee doc
          if (canCollectFees) {
            try {
              await deleteDoc(doc(db, "clubs", club.id, "matches", matchId, "fees", withdrawn.userId));
            } catch {
              // Fee doc may not exist
            }
          }

          // Create fee for replacement player
          try {
            const feeSettingsSnap = await getDoc(doc(db, "clubs", club.id, "settings", "fees"));
            const feeSettings = feeSettingsSnap.exists() ? feeSettingsSnap.data() : null;

            if (feeSettings && canCollectFees) {
              const defaultSeniorFee = feeSettings.feesByTeam?.[match.team] || feeSettings.defaultMatchFee || 0;
              const defaultJuniorFee = feeSettings.juniorFeesByTeam?.[match.team] || feeSettings.defaultJuniorFee || 0;

              const memberSnap = await getDoc(doc(db, "clubs", club.id, "members", replacement.userId));
              const isJunior = memberSnap.exists() ? (memberSnap.data().isJunior || false) : false;
              const feeAmount = isJunior ? defaultJuniorFee : defaultSeniorFee;

              if (feeAmount > 0) {
                await setDoc(doc(db, "clubs", club.id, "matches", matchId, "fees", replacement.userId), {
                  userId: replacement.userId,
                  displayName: replacement.displayName,
                  email: replacement.email || "",
                  amount: feeAmount * 100,
                  isJunior,
                  paid: false,
                  paidAt: null,
                  stripePaymentIntentId: null,
                  clubId: club.id,
                  clubName: club.name,
                  matchDate: match.date,
                  matchName: `${match.team} vs ${match.opponent}`,
                  team: match.team,
                  createdAt: Timestamp.now(),
                });
              }
            }
          } catch (feeErr) {
            console.error("Fee creation error:", feeErr);
          }

          // Create replacement feed post
          await createPost(club.id, {
            authorId: user.uid,
            authorName: membership.displayName,
            authorRole: membership.role,
            text: `\ud83d\udd04 ${replacement.displayName} replaces ${withdrawn.displayName} in ${match.team}`,
            visibility: "internal",
            clubName: club.name,
          });
        }

        await alert({ title: "Team Updated", emoji: "\u2705", message: `Replacements confirmed for ${match.team}.` });
        router.back();
      } else {
        // Original flow: auto-create fee records for selected players
        // Only create fees if club has Stripe Connect set up
        const canCollectFees = club.collectMatchFees && club.stripeOnboardingComplete;
        try {
          const feeSettingsSnap = await getDoc(doc(db, "clubs", club.id, "settings", "fees"));
          const feeSettings = feeSettingsSnap.exists() ? feeSettingsSnap.data() : null;

          if (feeSettings && canCollectFees) {
            const defaultSeniorFee = feeSettings.feesByTeam?.[match.team] || feeSettings.defaultMatchFee || 0;
            const defaultJuniorFee = feeSettings.juniorFeesByTeam?.[match.team] || feeSettings.defaultJuniorFee || 0;

            // Get member docs to check isJunior
            for (const player of allPlayers) {
              if (!selected.has(player.userId)) continue;
              const memberSnap = await getDoc(doc(db, "clubs", club.id, "members", player.userId));
              const isJunior = memberSnap.exists() ? (memberSnap.data().isJunior || false) : false;
              const feeAmount = isJunior ? defaultJuniorFee : defaultSeniorFee;

              if (feeAmount > 0) {
                await setDoc(doc(db, "clubs", club.id, "matches", matchId, "fees", player.userId), {
                  userId: player.userId,
                  displayName: player.displayName,
                  email: player.email || "",
                  amount: feeAmount * 100, // store in pence
                  isJunior,
                  paid: false,
                  paidAt: null,
                  stripePaymentIntentId: null,
                  clubId: club.id,
                  clubName: club.name,
                  matchDate: match.date,
                  matchName: `${match.team} vs ${match.opponent}`,
                  team: match.team,
                  createdAt: Timestamp.now(),
                });
              }
            }
          }
        } catch (feeErr) {
          console.error("Fee creation error:", feeErr);
          // Don't block team confirmation if fee creation fails
        }

        await alert({ title: "Team Confirmed", emoji: "\u2705", message: `${selected.size} players selected for ${match.team}.` });
        router.back();
      }
    } catch (err: any) {
      await alert({ title: "Error", emoji: "\ud83d\ude15", message: err.message || "Failed to confirm team.", buttonColor: "#dc2626" });
    } finally {
      setSaving(false);
    }
  };

  /* ── Loading / Error States ── */

  if (matchLoading || availLoading || hasAccess === null) {
    return <Center><ActivityIndicator size="large" color={colors.brand} /></Center>;
  }

  if (!match) {
    return (
      <Center>
        <EmptyText>Match not found.</EmptyText>
      </Center>
    );
  }

  if (!hasAccess) {
    return (
      <Center>
        <LockIcon size={32} color={colors.inkMuted} />
        <AccessDenied>You don't have permission to select the team.</AccessDenied>
        <EmptyText style={{ marginTop: 8 }}>Only the captain, vice-captain, or an admin can do this.</EmptyText>
      </Center>
    );
  }

  /* ── Render ── */

  const activeSelectedCount = selected.size;
  const withdrawnCount = withdrawnPlayers.length;
  const emptySlotsCount = withdrawnCount - [...selected].filter((id) => !withdrawnIds.has(id) && !allPlayers.find((p) => p.userId === id && withdrawnIds.has(id))).length;

  return (
    <Screen showsVerticalScrollIndicator={false}>
      <ContentWidth>
        {/* Match header */}
        <MatchHeader>
          <TeamBadge>
            <TeamBadgeText>{match.team}</TeamBadgeText>
          </TeamBadge>
          <Opponent>vs {match.opponent}</Opponent>
          <MatchMeta>{formatDate(match.date)} {"\u00B7"} {formatTime(match.time)}</MatchMeta>
        </MatchHeader>

        {/* Selection counter */}
        <CounterRow>
          <CounterText>Players selected</CounterText>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <CounterNumber full={activeSelectedCount >= 11}>
              {activeSelectedCount}/11
            </CounterNumber>
            {withdrawnCount > 0 && (
              <CounterSub>({withdrawnCount} withdrawn)</CounterSub>
            )}
          </View>
        </CounterRow>

        {/* Withdrawn players section (only when team already selected) */}
        {withdrawnPlayers.length > 0 && (
          <Section>
            <SectionLabel>{"\u26A0\uFE0F"} Withdrawn ({withdrawnPlayers.length})</SectionLabel>
            <PlayerCard>
              {withdrawnPlayers.map((player, i) => (
                <WithdrawnPlayerRow key={player.userId} hasBorder={i < withdrawnPlayers.length - 1}>
                  <PlayerAvatar bg={colors.dangerLight}>
                    <PlayerAvatarText color={colors.danger}>
                      {player.displayName?.[0]?.toUpperCase()}
                    </PlayerAvatarText>
                  </PlayerAvatar>
                  <Flex1>
                    <WithdrawnName>{player.displayName}</WithdrawnName>
                    <WithdrawnReasonText>
                      Withdrawn{player.withdrawalReason ? ` — ${player.withdrawalReason}` : ""}
                    </WithdrawnReasonText>
                    {player.replacedByName && (
                      <PlayerNote>Replaced by {player.replacedByName}</PlayerNote>
                    )}
                  </Flex1>
                  <CloseCircleFilledIcon size={20} color={colors.danger} />
                </WithdrawnPlayerRow>
              ))}
            </PlayerCard>
          </Section>
        )}

        {/* Available players */}
        <Section>
          <SectionLabel>Available ({available.length})</SectionLabel>
          {available.length > 0 ? (
            <PlayerCard>
              {available.map((player, i) => {
                const isSelected = selected.has(player.userId);
                const higherTeam = higherTeamSelections[player.userId];
                const isWithdrawn = withdrawnIds.has(player.userId);
                // Skip withdrawn players from available list — they show in the withdrawn section
                if (isWithdrawn) return null;
                return (
                  <PlayerRow
                    key={player.userId}
                    hasBorder={i < available.length - 1}
                    onPress={() => togglePlayer(player.userId)}
                  >
                    <PlayerAvatar bg={higherTeam ? colors.amber50 : colors.brand50}>
                      <PlayerAvatarText color={higherTeam ? colors.amber : colors.brandDark}>
                        {player.displayName?.[0]?.toUpperCase()}
                      </PlayerAvatarText>
                    </PlayerAvatar>
                    <Flex1>
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                        <PlayerName style={higherTeam ? { color: colors.inkMuted } : undefined}>
                          {player.displayName}
                        </PlayerName>
                        {higherTeam && (
                          <HigherTeamPill>
                            <HigherTeamPillText>Selected for {higherTeam}</HigherTeamPillText>
                          </HigherTeamPill>
                        )}
                      </View>
                      {player.note ? <PlayerNote>{player.note}</PlayerNote> : null}
                    </Flex1>
                    <CheckBox checked={isSelected}>
                      {isSelected && (
                        <CheckmarkIcon size={16} color={colors.white} />
                      )}
                    </CheckBox>
                  </PlayerRow>
                );
              })}
            </PlayerCard>
          ) : (
            <EmptySection>No players available</EmptySection>
          )}
        </Section>

        {/* Maybe players */}
        {maybe.length > 0 && (
          <Section>
            <SectionLabel>Maybe ({maybe.length})</SectionLabel>
            <PlayerCard>
              {maybe.map((player, i) => {
                const isSelected = selected.has(player.userId);
                const higherTeam = higherTeamSelections[player.userId];
                const isWithdrawn = withdrawnIds.has(player.userId);
                if (isWithdrawn) return null;
                return (
                  <PlayerRow
                    key={player.userId}
                    hasBorder={i < maybe.length - 1}
                    onPress={() => togglePlayer(player.userId)}
                  >
                    <PlayerAvatar bg={higherTeam ? colors.amber50 : colors.amber50}>
                      <PlayerAvatarText color={colors.amber}>
                        {player.displayName?.[0]?.toUpperCase()}
                      </PlayerAvatarText>
                    </PlayerAvatar>
                    <Flex1>
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                        <PlayerName style={higherTeam ? { color: colors.inkMuted } : undefined}>
                          {player.displayName}
                        </PlayerName>
                        {higherTeam && (
                          <HigherTeamPill>
                            <HigherTeamPillText>Selected for {higherTeam}</HigherTeamPillText>
                          </HigherTeamPill>
                        )}
                      </View>
                      {player.note ? <PlayerNote>{player.note}</PlayerNote> : null}
                    </Flex1>
                    <CheckBox checked={isSelected}>
                      {isSelected && (
                        <CheckmarkIcon size={16} color={colors.white} />
                      )}
                    </CheckBox>
                  </PlayerRow>
                );
              })}
            </PlayerCard>
          </Section>
        )}

        {/* Confirm button */}
        <ConfirmButton
          disabled={activeSelectedCount === 0 || saving}
          onPress={handleConfirm}
        >
          {saving ? (
            <ActivityIndicator size="small" color={colors.white} />
          ) : (
            <ConfirmButtonText disabled={activeSelectedCount === 0}>
              {isReplacementMode
                ? `Confirm Replacements (${activeSelectedCount}/11)`
                : `Confirm Team (${activeSelectedCount}/11)`}
            </ConfirmButtonText>
          )}
        </ConfirmButton>

        <BottomSpacer />
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </Screen>
  );
}
